import React, { useState } from 'react'
import {
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    TextField,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Switch,
    FormControlLabel,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useRentRoll } from '../../hooks'
import { AttributesTabHeader } from './AttributesTabHeader'
import { useAttributesRootStyles } from './styles'

interface AttributesTabProps {
    auditId: number
}

export const AttributesRoot: React.FC<AttributesTabProps> = ({ auditId }) => {
    const classes = useAttributesRootStyles()
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingPromptId, setEditingPromptId] = useState<number | null>(null)
    const [promptDraft, setPromptDraft] = useState('')

    const {
        attributes,
        isLoadingAttributes,
        updateAttribute,
        completeAttributeSelection,
        isCompletingAttributeSelection,
    } = useRentRoll({
        auditId,
    })

    const handleEditPrompt = (
        attributeId: number,
        currentPrompt: string,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation()
        setEditingPromptId(attributeId)
        setPromptDraft(currentPrompt || '')
    }

    const handleSavePrompt = () => {
        if (editingPromptId) {
            updateAttribute({
                attributeId: editingPromptId,
                prompt: promptDraft,
            })
        }
        setEditingPromptId(null)
    }

    const handleClosePromptDialog = () => {
        setEditingPromptId(null)
    }

    const handleCompleteAttributeSelection = () => {
        completeAttributeSelection()
    }

    if (isLoadingAttributes) {
        return (
            <Box className={classes.loadingState}>
                <CircularProgress />
            </Box>
        )
    }

    // Summary View (default)
    if (!isEditMode) {
        return (
            <Box className={classes.container}>
                <AttributesTabHeader
                    isEditMode={false}
                    attributes={attributes}
                    isCompletingAttributeSelection={
                        isCompletingAttributeSelection
                    }
                    onCompleteSelection={handleCompleteAttributeSelection}
                    onToggleEditMode={() => setIsEditMode(true)}
                />

                {/* Summary List */}
                {attributes.map((attr) => {
                    const isUnmatched = (attr.should_display as any) === null
                    return (
                        <Paper
                            key={attr.id}
                            className={`${classes.summaryCard} ${
                                isUnmatched ? classes.unmatchedAttribute : ''
                            }`}
                        >
                            <Typography
                                className={classes.summaryAttributeName}
                            >
                                {attr.name}
                            </Typography>
                            <Box className={classes.badgeContainer}>
                                {/* Column 0: Displaying */}
                                <Box className={classes.badgeSlot}>
                                    {attr.should_display && (
                                        <Chip
                                            label="Displaying"
                                            size="small"
                                            className={classes.displayBadge}
                                        />
                                    )}
                                </Box>
                                {/* Column 1: Extracting */}
                                <Box className={classes.badgeSlot}>
                                    {attr.should_extract && (
                                        <Chip
                                            label="Extracting"
                                            size="small"
                                            className={classes.extractBadge}
                                        />
                                    )}
                                </Box>
                                {/* Column 2: Financial */}
                                <Box className={classes.badgeSlot}>
                                    {attr.data_type === 'CURRENCY' && (
                                        <Chip
                                            label="Financial"
                                            size="small"
                                            className={classes.financialBadge}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    )
                })}
            </Box>
        )
    }

    // Edit Mode
    return (
        <Box className={classes.container}>
            <AttributesTabHeader
                isEditMode={true}
                attributes={attributes}
                isCompletingAttributeSelection={isCompletingAttributeSelection}
                onCompleteSelection={handleCompleteAttributeSelection}
                onToggleEditMode={() => setIsEditMode(false)}
            />

            {/* Attributes List */}
            {attributes.map((attr) => (
                <Paper key={attr.id} className={classes.attributeCard}>
                    {/* Attribute Name */}
                    <Typography className={classes.attributeName}>
                        {attr.name}
                    </Typography>

                    {/* Prompt Section - Only show if extracting */}
                    {attr.should_extract && (
                        <div className={classes.attributePrompt}>
                            <IconButton
                                size="small"
                                onClick={(e) =>
                                    handleEditPrompt(attr.id, attr.prompt, e)
                                }
                            >
                                <span role="img" aria-label="edit">
                                    ✎
                                </span>
                            </IconButton>
                            <Typography
                                variant="body2"
                                style={{
                                    flex: 1,
                                    marginRight: 4,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {attr.prompt || 'No prompt'}
                            </Typography>
                        </div>
                    )}

                    {/* Spacer when not extracting */}
                    {!attr.should_extract && <div style={{ flex: 1 }} />}

                    {/* Controls Group */}
                    <Box className={classes.controlsGroup}>
                        {/* Data Type Selector */}
                        <TextField
                            select
                            value={attr.data_type || 'TEXT'}
                            onChange={(e) =>
                                updateAttribute({
                                    attributeId: attr.id,
                                    data_type: e.target.value.toUpperCase(),
                                })
                            }
                            size="small"
                            variant="outlined"
                            style={{ width: 120 }}
                        >
                            <MenuItem value="TEXT">Text</MenuItem>
                            <MenuItem value="NUMBER">Number</MenuItem>
                            <MenuItem value="CURRENCY">Currency</MenuItem>
                            <MenuItem value="DATE">Date</MenuItem>
                            <MenuItem value="BOOLEAN">Boolean</MenuItem>
                        </TextField>

                        {/* Extract Toggle */}
                        <FormControlLabel
                            className={classes.toggleControl}
                            control={
                                <Switch
                                    checked={attr.should_extract}
                                    onChange={() =>
                                        updateAttribute({
                                            attributeId: attr.id,
                                            should_extract: !attr.should_extract,
                                        })
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography className={classes.toggleLabel}>
                                    Extract
                                </Typography>
                            }
                        />

                        {/* Display Toggle */}
                        <FormControlLabel
                            className={classes.toggleControl}
                            control={
                                <Switch
                                    checked={attr.should_display}
                                    onChange={() =>
                                        updateAttribute({
                                            attributeId: attr.id,
                                            should_display: !attr.should_display,
                                        })
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography className={classes.toggleLabel}>
                                    Display
                                </Typography>
                            }
                        />
                    </Box>
                </Paper>
            ))}

            {/* Prompt Edit Dialog */}
            <Dialog
                open={Boolean(editingPromptId)}
                onClose={handleClosePromptDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Prompt</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Prompt"
                        value={promptDraft}
                        onChange={(e) => setPromptDraft(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleSavePrompt}
                        color="primary"
                        variant="contained"
                        size="small"
                    >
                        Save
                    </Button>
                    <Button onClick={handleClosePromptDialog} size="small">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
